import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { relativeTime } from "@/lib/utils";
import {
  MessageSquarePlus, Loader2, Trash2, ThumbsUp, X, RefreshCw, Flame,
  ExternalLink, Plus, Building2, User, Sparkles, Check,
} from "lucide-react";

const TABS = [
  { key: "pending_review", label: "Pending review" },
  { key: "posted", label: "Posted" },
  { key: "dismissed", label: "Dismissed" },
];

export default function LinkedInEngagePage() {
  const [cfg, setCfg] = useState(null);
  const [targets, setTargets] = useState([]);
  const [engagements, setEngagements] = useState([]);
  const [tab, setTab] = useState("pending_review");
  const [newUrl, setNewUrl] = useState("");
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [loadingSugg, setLoadingSugg] = useState(false);
  const [addingSugg, setAddingSugg] = useState(false);

  const fetchSuggestions = async () => {
    setLoadingSugg(true);
    try {
      const { data } = await api.get("/linkedin-engage/suggestions");
      if (!data.ok) {
        toast.error(data.error === "no_connected_linkedin_account" ? "Connect LinkedIn first" : "Could not read your LinkedIn");
        setLoadingSugg(false); return;
      }
      setSuggestions(data.suggestions || []);
      // pre-select the logistics-relevant ones
      setSelected(new Set((data.suggestions || []).filter((s) => s.relevant).map((s) => s.identifier)));
      toast.info(`Found ${data.relevant_count} logistics contacts in your network`, {
        description: "Tick the ones to engage, then Add selected.",
      });
    } catch (e) { toast.error("Could not fetch suggestions"); }
    setLoadingSugg(false);
  };

  const toggleSel = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const addSelected = async () => {
    const items = (suggestions || []).filter((s) => selected.has(s.identifier));
    if (!items.length) { toast.error("Select at least one"); return; }
    setAddingSugg(true);
    try {
      const { data } = await api.post("/linkedin-engage/targets/bulk", { items });
      toast.success(`Added ${data.added} to your watch list`);
      setSuggestions(null); setSelected(new Set());
      loadTargets();
    } catch (e) { toast.error("Could not add selected"); }
    setAddingSugg(false);
  };

  const loadCfg = () => api.get("/linkedin-engage/settings").then((r) => setCfg(r.data)).catch(() => {});
  const loadTargets = () => api.get("/linkedin-engage/targets").then((r) => setTargets(r.data.targets || [])).catch(() => {});
  const loadEngagements = (status) => api.get(`/linkedin-engage/engagements?status=${status}`).then((r) => setEngagements(r.data.engagements || [])).catch(() => {});

  useEffect(() => { loadCfg(); loadTargets(); }, []);
  useEffect(() => { loadEngagements(tab); }, [tab]);

  const saveCfg = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.post("/linkedin-engage/settings", patch);
      setCfg(data);
      toast.success("Settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setSaving(false);
  };

  const resolvePage = async () => {
    if (!cfg?.page_url) { toast.error("Enter the company page URL first"); return; }
    setResolving(true);
    try {
      const { data } = await api.post("/linkedin-engage/resolve-page", { page_url: cfg.page_url });
      if (data.ok) {
        await saveCfg({ organization_urn: data.organization_urn, page_name: data.name });
        toast.success(`Resolved: ${data.organization_urn}`);
      } else {
        toast.error(data.error === "could_not_resolve" ? "Couldn't auto-resolve — paste the organization URN manually" : (data.error || "Failed"));
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Resolve failed"); }
    setResolving(false);
  };

  const addTarget = async (e) => {
    e.preventDefault();
    if (!newUrl.trim()) return;
    try {
      await api.post("/linkedin-engage/targets", { url: newUrl.trim() });
      setNewUrl("");
      loadTargets();
      toast.success("Added to watch list");
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid LinkedIn URL"); }
  };

  const removeTarget = async (id) => {
    try { await api.delete(`/linkedin-engage/targets/${id}`); loadTargets(); }
    catch { toast.error("Could not remove"); }
  };

  const scanNow = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/linkedin-engage/scan");
      toast.info("Scanning posts for viral content…");
      const poll = setInterval(async () => {
        try {
          const { data: job } = await api.get(`/linkedin-engage/scan/${data.job_id}`);
          if (job.status === "done") {
            clearInterval(poll); setScanning(false);
            const st = job.result?.stats || {};
            const scanned = st.authors_scanned || 0;
            if (!job.result?.ok && job.result?.error === "no_connected_linkedin_account") {
              toast.error("No LinkedIn account connected", { description: "Connect Rok's LinkedIn under the LinkedIn page first." });
            } else if (scanned === 0) {
              toast.warning("Nothing to scan yet", {
                description: "Add people or company pages to your Watch list (top-right), then scan again. Prospects are only scanned if they have a resolved LinkedIn ID.",
              });
            } else if ((st.eligible || 0) === 0) {
              toast.info("Scan complete — no viral posts found", {
                description: `Checked ${scanned} author(s) · ${st.posts_seen || 0} recent posts. None hit your bar (≥${cfg?.engage_min_reactions} reactions or ≥${cfg?.engage_min_comments} comments) within ${cfg?.max_post_age_days} days.${st.errors ? ` ${st.errors} author(s) couldn't be read.` : ""}`,
              });
            } else {
              toast.success("Scan complete", {
                description: `${st.eligible} eligible · ${st.auto_posted || 0} auto-posted · ${st.queued || 0} queued for review`,
              });
            }
            loadEngagements(tab);
          } else if (job.status === "failed") {
            clearInterval(poll); setScanning(false);
            toast.error("Scan failed", { description: job.error || "Unknown error" });
          }
        } catch { /* keep polling */ }
      }, 3000);
      setTimeout(() => { clearInterval(poll); setScanning(false); }, 180000);
    } catch (e) { setScanning(false); toast.error(e?.response?.data?.detail || "Scan failed"); }
  };

  const updateDraft = (id, text) => setEngagements((prev) => prev.map((x) => (x.id === id ? { ...x, draft_comment: text } : x)));

  const approve = async (eng, asPage) => {
    try {
      const { data } = await api.post(`/linkedin-engage/engagements/${eng.id}/approve`,
        { text: eng.draft_comment, as_page: asPage });
      toast.success(data.posted_as === "page"
        ? "Commented as the Europacific page"
        : "Commented as you (Rok) on the post");
      loadEngagements(tab);
    } catch (e) {
      const err = e?.response?.data?.detail;
      toast.error(err === "no_company_page_configured"
        ? "Set the Europacific company page first (top of this screen)."
        : (err || "Could not post"));
    }
  };

  const reject = async (eng) => {
    try { await api.post(`/linkedin-engage/engagements/${eng.id}/reject`); loadEngagements(tab); }
    catch { toast.error("Could not dismiss"); }
  };

  const orgMissing = !cfg?.organization_urn;

  return (
    <DashboardLayout
      eyebrow="LinkedIn"
      title="Engage as Europacific"
      actions={
        <Button onClick={scanNow} disabled={scanning} data-testid="engage-scan-btn" className="rounded-sm">
          {scanning ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Scanning…</> : <><RefreshCw className="w-4 h-4 mr-1.5" /> Scan now</>}
        </Button>
      }
    >
      <p className="text-sm text-muted-foreground mb-6 max-w-3xl">
        Grow visibility on viral posts from people & companies you watch (plus your prospects).
        Very-viral posts get an auto-comment <strong>as the Europacific company page</strong>;
        everything else lands in the review queue where you choose <strong>Comment as the page</strong>
        or <strong>Comment as you (Rok)</strong>. These are comments on the original viral posts — we never post to your own page feed.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
        {/* Settings */}
        <div className="lg:col-span-2 border border-border rounded-sm p-5" data-testid="engage-settings">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold">Configuration</h3>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{cfg?.enabled ? "Auto-scan ON" : "Auto-scan OFF"}</span>
              <Switch checked={!!cfg?.enabled} onCheckedChange={(v) => saveCfg({ enabled: v })} data-testid="engage-enabled-toggle" />
            </div>
          </div>

          {orgMissing && (
            <div className="mb-4 text-xs rounded-sm border border-amber-accent/40 bg-amber-accent/10 text-amber-accent p-3">
              Set the Europacific company page below so comments post <strong>as the page</strong>. Rok's LinkedIn account must be an Admin/Content Admin of that page.
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label className="text-xs">Europacific company page URL</Label>
              <div className="flex gap-2 mt-1">
                <Input value={cfg?.page_url || ""} placeholder="https://www.linkedin.com/company/europacific/"
                  onChange={(e) => setCfg((c) => ({ ...c, page_url: e.target.value }))}
                  data-testid="engage-page-url" />
                <Button variant="outline" size="sm" onClick={resolvePage} disabled={resolving} data-testid="engage-resolve-btn">
                  {resolving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Resolve"}
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs">Organization URN (comment author)</Label>
              <Input value={cfg?.organization_urn || ""} placeholder="urn:li:organization:123456"
                onChange={(e) => setCfg((c) => ({ ...c, organization_urn: e.target.value }))}
                onBlur={(e) => saveCfg({ organization_urn: e.target.value })}
                data-testid="engage-org-urn" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <NumField label="Engage if reactions ≥" value={cfg?.engage_min_reactions} onSave={(v) => saveCfg({ engage_min_reactions: v })} testid="engage-min-reactions" />
              <NumField label="…or comments ≥" value={cfg?.engage_min_comments} onSave={(v) => saveCfg({ engage_min_comments: v })} testid="engage-min-comments" />
              <NumField label="Auto-post if reactions ≥" value={cfg?.auto_min_reactions} onSave={(v) => saveCfg({ auto_min_reactions: v })} testid="auto-min-reactions" />
              <NumField label="…or comments ≥" value={cfg?.auto_min_comments} onSave={(v) => saveCfg({ auto_min_comments: v })} testid="auto-min-comments" />
              <NumField label="Daily comment cap" value={cfg?.daily_cap} onSave={(v) => saveCfg({ daily_cap: v })} testid="engage-daily-cap" />
              <NumField label="Max post age (days)" value={cfg?.max_post_age_days} onSave={(v) => saveCfg({ max_post_age_days: v })} testid="engage-max-age" />
              <NumField label="Max per author / 30d" value={cfg?.max_per_author} onSave={(v) => saveCfg({ max_per_author: v })} testid="engage-max-per-author" />
            </div>

            <div className="flex items-center justify-between pt-1">
              <Label className="text-xs">Also engage posts from my prospects / partners</Label>
              <Switch checked={!!cfg?.include_prospects} onCheckedChange={(v) => saveCfg({ include_prospects: v })} data-testid="engage-include-prospects" />
            </div>
          </div>
        </div>

        {/* Watch list */}
        <div className="border border-border rounded-sm p-5" data-testid="engage-watchlist">
          <h3 className="font-display font-semibold mb-3">Watch list</h3>
          <form onSubmit={addTarget} className="flex gap-2 mb-2">
            <Input value={newUrl} onChange={(e) => setNewUrl(e.target.value)}
              placeholder="LinkedIn profile or company URL" data-testid="engage-add-target-input" />
            <Button type="submit" size="sm" variant="outline" data-testid="engage-add-target-btn"><Plus className="w-3.5 h-3.5" /></Button>
          </form>
          <Button onClick={fetchSuggestions} disabled={loadingSugg} size="sm" variant="secondary"
            className="w-full mb-3 rounded-sm" data-testid="engage-suggest-btn">
            {loadingSugg ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Reading your network…</>
              : <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Suggest from my LinkedIn</>}
          </Button>

          {suggestions && (
            <div className="mb-3 border border-border rounded-sm p-2" data-testid="engage-suggestions">
              <div className="flex items-center justify-between mb-2 px-1">
                <span className="text-xs text-muted-foreground">{selected.size} selected · logistics pre-ticked</span>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => { setSuggestions(null); setSelected(new Set()); }} data-testid="engage-suggest-cancel">Cancel</Button>
                  <Button size="sm" className="h-7 px-2" onClick={addSelected} disabled={addingSugg || selected.size === 0} data-testid="engage-suggest-add">
                    {addingSugg ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : `Add selected (${selected.size})`}
                  </Button>
                </div>
              </div>
              <div className="space-y-1 max-h-80 overflow-y-auto">
                {suggestions.map((s) => (
                  <button key={s.identifier} onClick={() => toggleSel(s.identifier)} type="button"
                    className={`w-full flex items-start gap-2 text-left text-sm rounded-sm px-2 py-1.5 transition-colors ${selected.has(s.identifier) ? "bg-signal-500/10 border border-signal-500/40" : "border border-transparent hover:bg-muted/40"}`}
                    data-testid={`engage-sugg-${s.identifier}`}>
                    <span className={`mt-0.5 w-4 h-4 shrink-0 rounded-sm border flex items-center justify-center ${selected.has(s.identifier) ? "bg-signal-500 border-signal-500 text-white" : "border-muted-foreground/40"}`}>
                      {selected.has(s.identifier) && <Check className="w-3 h-3" />}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate font-medium flex items-center gap-1.5">
                        {s.name}{s.relevant && <Flame className="w-3 h-3 text-signal-500 inline" />}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">{s.headline || "—"}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {targets.length === 0 && !suggestions && <p className="text-xs text-muted-foreground">Add the people & company pages whose posts you want to engage — or click <strong>Suggest from my LinkedIn</strong>.</p>}
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {targets.map((t) => (
              <div key={t.id} className="flex items-center gap-2 text-sm border border-border rounded-sm px-2.5 py-1.5" data-testid={`engage-target-${t.id}`}>
                {t.type === "company" ? <Building2 className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> : <User className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                <span className="truncate flex-1">{t.name}</span>
                <button onClick={() => removeTarget(t.id)} className="text-muted-foreground hover:text-destructive" data-testid={`engage-remove-target-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Engagements */}
      <div className="flex items-center gap-1 mb-4 border-b border-border" data-testid="engage-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`engage-tab-${t.key}`}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${tab === t.key ? "border-signal-500 text-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {engagements.length === 0 && (
        <div className="p-10 text-sm text-center text-muted-foreground border border-border rounded-sm" data-testid="engage-empty">
          {tab === "pending_review" ? "No posts queued. Click 'Scan now' to find viral posts to engage." : "Nothing here yet."}
        </div>
      )}

      <div className="space-y-3" data-testid="engage-list">
        {engagements.map((e) => (
          <div key={e.id} className="border border-border rounded-sm p-4" data-testid={`engage-card-${e.id}`}>
            <div className="flex items-start justify-between gap-4 mb-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">{e.author_name || e.author_identifier}</span>
                  {e.is_viral && <span className="inline-flex items-center gap-1 text-[0.65rem] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-destructive/10 text-destructive border border-destructive/30"><Flame className="w-3 h-3" /> viral</span>}
                  <span className="text-xs text-muted-foreground">· {e.reactions} reactions · {e.comments} comments</span>
                  {e.author_source === "prospect" && <span className="text-[0.65rem] uppercase tracking-wider text-muted-foreground">prospect</span>}
                </div>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{e.post_excerpt}</p>
              </div>
              {e.post_url && <a href={e.post_url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground shrink-0" data-testid={`engage-postlink-${e.id}`}><ExternalLink className="w-4 h-4" /></a>}
            </div>

            {tab === "pending_review" ? (
              <>
                <Textarea value={e.draft_comment || ""} onChange={(ev) => updateDraft(e.id, ev.target.value)}
                  className="text-sm mt-1 mb-2" rows={3} data-testid={`engage-draft-${e.id}`} />
                {e.last_error && <p className="text-xs text-destructive mb-2">Last attempt failed: {e.last_error}</p>}
                <p className="text-[0.7rem] text-muted-foreground mb-1.5">
                  Choose how to publish — {e.suggested_identity === "page" ? <strong>recommended: as the page</strong> : <><strong>recommended: as you</strong> (auto-tags @{cfg?.page_name || "Europacific"})</>} to keep it varied.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant={e.suggested_identity === "page" ? "default" : "outline"}
                    onClick={() => approve(e, true)} disabled={!e.draft_comment || orgMissing}
                    title={orgMissing ? "Set the Europacific company page first" : "Comment on the viral post as the Europacific company page"}
                    data-testid={`engage-approve-page-${e.id}`}>
                    <Building2 className="w-3.5 h-3.5 mr-1.5" /> Comment as Europacific page
                  </Button>
                  <Button size="sm" variant={e.suggested_identity === "page" ? "outline" : "default"}
                    onClick={() => approve(e, false)} disabled={!e.draft_comment}
                    title="Comment on the original post from your personal profile (Rok), tagging the Europacific page"
                    data-testid={`engage-approve-me-${e.id}`}>
                    <User className="w-3.5 h-3.5 mr-1.5" /> Comment as me (@{cfg?.page_name || "Europacific"})
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => reject(e)} data-testid={`engage-reject-${e.id}`}>
                    <X className="w-3.5 h-3.5 mr-1.5" /> Dismiss
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-sm bg-muted/30 border border-border rounded-sm p-3 mt-1">
                <MessageSquarePlus className="w-3.5 h-3.5 inline mr-1.5 text-muted-foreground" />
                {e.draft_comment}
                {e.posted_at && (
                  <span className="block text-xs text-muted-foreground mt-1">
                    {e.decided_by === "auto" ? "Auto-posted" : "Posted"} {relativeTime(e.posted_at)}
                    {" · "}
                    {e.posted_as === "personal" ? "as Rok (personal comment)" : "as Europacific page (comment)"}
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}

function NumField({ label, value, onSave, testid }) {
  const [v, setV] = useState(value ?? "");
  useEffect(() => { setV(value ?? ""); }, [value]);
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input type="number" value={v} onChange={(e) => setV(e.target.value)}
        onBlur={() => { const n = parseInt(v, 10); if (!isNaN(n) && n !== value) onSave(n); }}
        className="mt-1" data-testid={testid} />
    </div>
  );
}
